/**
 * Semantic Color Tokens
 *
 * Contextual color mappings that reference primitive colors.
 * These provide meaning (background, foreground, brand, etc.)
 *
 * @see DS-001-colors.md
 * @figma https://www.figma.com/design/NXGWL5ErhbsA0kqoybqQbg/Design-System?node-id=45-9609
 */

import { ctaOrangeColors, primitiveColors, purpleGradientColors } from "./primitives";

export const semanticColors = {
  neutral: {
    background: {
      default: primitiveColors.grey.white,
      intermediate: primitiveColors.grey[100],
      attenuated: primitiveColors.grey[200],
      inverse: primitiveColors.grey[900],
    },
    foreground: {
      default: primitiveColors.grey[800],
      intermediate: primitiveColors.grey[600],
      attenuated: primitiveColors.grey[400],
      inverse: primitiveColors.grey.white,
    },
    border: {
      default: primitiveColors.grey[300],
      lowEmphasis: primitiveColors.grey[100],
      mediumEmphasis: primitiveColors.grey[200],
      highEmphasis: primitiveColors.grey[300],
      // Figma's neutral/border-control: the enabled outline of an unchecked
      // checkbox or radio, dark enough for a control's 3:1 boundary on white
      control: primitiveColors.grey[600],
    },
  },
  brand: {
    default: primitiveColors.smalt[700],
    primary: primitiveColors.smalt[700],
    primaryHover: primitiveColors.smalt[800],
    onPrimary: primitiveColors.grey.white,
    primarySurface: primitiveColors.smalt[50],
    onPrimarySurface: primitiveColors.smalt[800],
    primaryBorder: primitiveColors.smalt[500],
    static: primitiveColors.smalt[900],
    staticHover: primitiveColors.smalt[800],
    onStatic: primitiveColors.grey.white,
    onStaticAttenuated: primitiveColors.smalt[100],
    staticBorder: primitiveColors.smalt[700],
    navbar: primitiveColors.smalt[900],
  },
  button: {
    cta: {
      border: ctaOrangeColors[200],
      borderHover: ctaOrangeColors[300],
      default: ctaOrangeColors[400],
      disabled: primitiveColors.grey[100],
      hover: ctaOrangeColors[300],
      onCta: primitiveColors.grey.white,
      onCtaDisabled: primitiveColors.grey[300],
      onSurface: ctaOrangeColors[800],
      pressed: ctaOrangeColors[200],
      surface: ctaOrangeColors[50],
      surfaceHover: ctaOrangeColors[100],
    },
    cta2: {
      default: primitiveColors.smalt[900],
      hover: primitiveColors.smalt[700],
      onCta2: primitiveColors.grey.white,
      border: primitiveColors.smalt[200],
      borderHover: primitiveColors.smalt[400],
      surface: primitiveColors.smalt[50],
      surfaceHover: primitiveColors.smalt[50],
      surfacePressed: primitiveColors.smalt[100],
      onSurface: primitiveColors.smalt[900],
      onCta2Disabled: primitiveColors.grey[300],
    },
    destructive: {
      border: primitiveColors.red[200],
      borderHover: primitiveColors.red[300],
      default: primitiveColors.red[500],
      hover: primitiveColors.red[600],
      onDestructive: primitiveColors.grey.white,
      onDestructiveDisabled: primitiveColors.grey[300],
      onSurface: primitiveColors.red[800],
      pressed: primitiveColors.red[400],
      surface: primitiveColors.red[50],
      surfacePressed: primitiveColors.red[100],
    },
    // Figma's button/CTAsuccess group, the keys success-secondary binds. Solid
    // success still binds system.success.* (same values; its pressed is green 700)
    success: {
      border: primitiveColors.green[200],
      borderHover: primitiveColors.green[300],
      default: primitiveColors.green[500],
      hover: primitiveColors.green[600],
      surface: primitiveColors.green[50],
      surfacePressed: primitiveColors.green[100],
    },
    // Figma's button/disabled group, shared by every button variant
    disabled: {
      ctaDisabled: primitiveColors.grey[100],
      ctaBorderDisabled: primitiveColors.grey[200],
      ctaTextDisabled: primitiveColors.grey[300],
      onCtaDisabled: primitiveColors.grey[300],
    },
  },
  system: {
    success: {
      default: primitiveColors.green[500],
      hover: primitiveColors.green[600],
      pressed: primitiveColors.green[700],
      onSuccess: primitiveColors.grey.white,
      border: primitiveColors.green[200],
      surface: primitiveColors.green[50],
      onSurface: primitiveColors.green[800],
    },
    info: {
      default: primitiveColors.blue[500],
      onInfo: primitiveColors.grey.white,
      border: primitiveColors.blue[300],
      surface: primitiveColors.blue[50],
      onSurface: primitiveColors.blue[800],
    },
    danger: {
      default: primitiveColors.red[500],
      onDanger: primitiveColors.grey.white,
      border: primitiveColors.red[200],
      surface: primitiveColors.red[50],
      onSurface: primitiveColors.red[800],
    },
    warning: {
      default: primitiveColors.yellow[400],
      onWarning: primitiveColors.grey.white,
      border: primitiveColors.yellow[200],
      surface: primitiveColors.yellow[50],
      onSurface: primitiveColors.yellow[800],
    },
  },
  overlay: {
    20: `${primitiveColors.grey[950]}33`,
    50: `${primitiveColors.grey[950]}80`,
    80: `${primitiveColors.grey[950]}cc`,
  },
  gradient: {
    blue: {
      start: primitiveColors.smalt[500],
      end: primitiveColors.smalt[700],
    },
    orange: {
      start: ctaOrangeColors[300],
      end: ctaOrangeColors[400],
    },
    purple: {
      start: purpleGradientColors.start,
      end: purpleGradientColors.end,
    },
  },
} as const;
